// frontend/src/components/common/BulkActionsToolbar.js
// Reusable bulk-actions toolbar shown when complaints are selected in a list.
import React, { useState } from 'react';
import api from '../../services/api';
import * as XLSX from 'xlsx';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Alert,
  TextField,
  Typography,
} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { normalizePriority } from './PriorityChip';

const STATUS_OPTIONS = ['New', 'Open', 'Pending', 'Solved', 'Resolved'];

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const BulkActionsToolbar = ({ selectedIds, complaints, onDone, isAdmin }) => {
  // Status dialog state
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkRemarks, setBulkRemarks] = useState('');
  const [sendSms, setSendSms] = useState(false);
  const [statusSubmitting, setStatusSubmitting] = useState(false);

  // Assign dialog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignUsers, setAssignUsers] = useState([]);
  const [assignUsersLoading, setAssignUsersLoading] = useState(false);
  const [assignTo, setAssignTo] = useState('');
  const [assignNote, setAssignNote] = useState('');
  const [assignSubmitting, setAssignSubmitting] = useState(false);

  // Result snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  if (!selectedIds || selectedIds.length === 0) return null;

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  // --- Change Status ---
  const openStatusDialog = () => {
    setBulkStatus('');
    setBulkRemarks('');
    setSendSms(false);
    setStatusDialogOpen(true);
  };

  const handleStatusConfirm = async () => {
    if (!bulkStatus) return;
    setStatusSubmitting(true);
    try {
      const res = await api.post('/complaints/bulk/status', {
        ids: selectedIds,
        status: bulkStatus,
        remarks: bulkRemarks || undefined,
        sendSms,
      });
      const { updated = 0, skipped = 0, failed = [], smsSent = 0 } = res.data || {};
      let message = `${updated} updated, ${skipped} skipped, ${failed.length} failed`;
      if (sendSms) message += ` — ${smsSent} SMS sent`;
      showSnackbar(message, failed.length > 0 ? 'warning' : 'success');
      setStatusDialogOpen(false);
      if (onDone) onDone();
    } catch (err) {
      console.error('Error updating status in bulk:', err);
      showSnackbar(err.response?.data?.message || 'Bulk status update failed', 'error');
    } finally {
      setStatusSubmitting(false);
    }
  };

  // --- Assign ---
  const openAssignDialog = () => {
    setAssignTo('');
    setAssignNote('');
    setAssignDialogOpen(true);
    // Lazily fetch users the first time the dialog opens.
    if (assignUsers.length === 0) {
      setAssignUsersLoading(true);
      api
        .get('/users')
        .then((res) => setAssignUsers(res.data || []))
        .catch((err) => {
          console.error('Error fetching users:', err);
          showSnackbar('Failed to load users list', 'error');
        })
        .finally(() => setAssignUsersLoading(false));
    }
  };

  const handleAssignConfirm = async () => {
    setAssignSubmitting(true);
    try {
      const res = await api.post('/complaints/bulk/assign', {
        ids: selectedIds,
        assignedTo: assignTo === 'unassigned' || assignTo === '' ? null : assignTo,
        note: assignNote || undefined,
      });
      const { updated = 0, skipped = 0, failed = [] } = res.data || {};
      showSnackbar(
        `${updated} updated, ${skipped} skipped, ${failed.length} failed`,
        failed.length > 0 ? 'warning' : 'success'
      );
      setAssignDialogOpen(false);
      if (onDone) onDone();
    } catch (err) {
      console.error('Error assigning complaints in bulk:', err);
      showSnackbar(err.response?.data?.message || 'Bulk assign failed', 'error');
    } finally {
      setAssignSubmitting(false);
    }
  };

  // --- Export Selected ---
  const handleExportSelected = () => {
    const exportData = (complaints || []).map((complaint) => ({
      'Complaint ID': complaint.complaintId || complaint._id,
      'Name': complaint.name || '',
      'Mobile': complaint.mobileNumber || '',
      'Category': complaint.category || complaint.categoryName || '',
      'Subject': complaint.subject || '',
      'Status': complaint.status || '',
      'Priority': normalizePriority(complaint.priority),
      'Assigned To': complaint.assignedTo?.username || '',
      'Panchayath': complaint.panchayath || '',
      'Ward': complaint.wardNo || '',
      'Created At': formatDate(complaint.createdAt || complaint.complaintDate),
      'Resolved At': formatDate(complaint.resolvedAt),
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Selected Complaints');
    XLSX.writeFile(workbook, 'selected-complaints.xlsx');
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>
        {selectedIds.length} selected
      </Typography>
      {isAdmin && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<EditNoteIcon />}
          onClick={openStatusDialog}
          aria-label="Change Status of Selected"
        >
          Change Status
        </Button>
      )}
      {isAdmin && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<AssignmentIndIcon />}
          onClick={openAssignDialog}
          aria-label="Assign Selected"
        >
          Assign
        </Button>
      )}
      <Button
        variant="outlined"
        size="small"
        startIcon={<FileDownloadIcon />}
        onClick={handleExportSelected}
        aria-label="Export Selected to Excel"
      >
        Export Selected
      </Button>

      {/* Change Status Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Change Status ({selectedIds.length} selected)</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="bulk-status-label">Status</InputLabel>
            <Select
              labelId="bulk-status-label"
              label="Status"
              value={bulkStatus}
              onChange={(e) => setBulkStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Remarks (optional)"
            value={bulkRemarks}
            onChange={(e) => setBulkRemarks(e.target.value)}
            fullWidth
            multiline
            rows={2}
            sx={{ mt: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={sendSms}
                onChange={(e) => setSendSms(e.target.checked)}
              />
            }
            label="Send status SMS to each citizen"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)} disabled={statusSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleStatusConfirm}
            disabled={!bulkStatus || statusSubmitting}
          >
            {statusSubmitting ? <CircularProgress size={20} /> : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Assign ({selectedIds.length} selected)</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="bulk-assign-label">Assign To</InputLabel>
            <Select
              labelId="bulk-assign-label"
              label="Assign To"
              value={assignTo}
              onChange={(e) => setAssignTo(e.target.value)}
            >
              <MenuItem value="unassigned">Unassigned</MenuItem>
              {assignUsersLoading ? (
                <MenuItem disabled>Loading users…</MenuItem>
              ) : (
                assignUsers.map((user) => (
                  <MenuItem key={user._id} value={user._id}>
                    {user.username}{user.role ? ` (${user.role})` : ''}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <TextField
            label="Note (optional)"
            value={assignNote}
            onChange={(e) => setAssignNote(e.target.value)}
            fullWidth
            multiline
            rows={2}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)} disabled={assignSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAssignConfirm}
            disabled={assignTo === '' || assignSubmitting}
          >
            {assignSubmitting ? <CircularProgress size={20} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BulkActionsToolbar;
